import React, { useContext, useEffect, useState } from "react";
import { useQuery } from "react-query";
import { useRouteMatch, useHistory } from "react-router-dom";

import { ProjectProvider } from "../../contexts/ProjectContext";
import { QuestionCommentProvider } from "../../contexts/QuestionCommentContext";
import { ProjectPermissionContextProvider, ProjectPermissionContext } from "../../contexts/ProjectPermissionContext";
import { DivisionProvider } from "../../contexts/DivisionContext";
import { AppDivisionContext, AppDivisionProvider } from "../../contexts/AppDivisionContext";
import { InspectionStore, InspectionContext } from "../../contexts/InspectionContext";
import { QuestionTypeContext } from "../../contexts/QuestionTypeContext";
import { ProjectContext } from "../../contexts/ProjectContext";
import { RolesContext } from "../../contexts/RolesContext";
import AppContext from "../../contexts/AppContext";

import PublicURLNavbar from "../PublicUrl/Component/PublicURLNavbar";
import { LoadingMessage } from "@sw-sw/lib-ui";

import { useQuestionTypes } from "../../hooks/questionTypes";
import projectApi from "../../utils/api/project";
import inspectionApi from "../../utils/api/inspection";


const NoLayoutWrapper = ({ children }) => (
    <AppDivisionProvider>
        <DivisionProvider>
            <ProjectProvider>
                <InspectionStore>
                    <QuestionCommentProvider>
                        <ProjectPermissionContextProvider>
                            <NoLayout>{children}</NoLayout>
                        </ProjectPermissionContextProvider>
                    </QuestionCommentProvider>
                </InspectionStore>
            </ProjectProvider>
        </DivisionProvider>
    </AppDivisionProvider>
);


const NoLayout = ({ children }) => {
    const inspectionContext = useContext(InspectionContext);
    const projectContext = useContext(ProjectContext);
    const projectPermissionContext = useContext(ProjectPermissionContext);
    const questionTypeContext = useContext(QuestionTypeContext);
    const permCheck = useContext(RolesContext).userHasPermission;
    const appStore = useContext(AppContext);
    const appDivisionContext = useContext(AppDivisionContext);

    const user = appStore.get("user");

    const match = useRouteMatch();
    const history = useHistory();

    const project_uuid = match.params?.project_uuid;
    const findingId = match.params?.findingId;
    const inspectionId = match.params?.inspectionId;

    const isGuest = history.location.pathname.startsWith("/guest") && user?.roleName === "Guest";

    const [loading, setLoading] = useState(true);
    const questionTypesQuery = useQuestionTypes();

    const Query = useQuery(
        ["inspection-project", { project_uuid }],
        async () => {

            const projectGuestData = await projectApi.fetchGuestProjectDetails(project_uuid);
            const { inspection, ...rest } = await inspectionApi.get(inspectionId);

            return {
                ...rest,
                inspection: {
                    ...inspection,
                    project: projectGuestData.project,
                },
                projectGuestData
            };

        },
        {
            enabled: !!project_uuid,
            refetchOnWindowFocus: false,
            keepPreviousData: false,
        }
    );

    useEffect(() => {
        if (Query.isFetched && Query.data) {
            const { inspection, template, projectGuestData } = Query.data;

            inspectionContext.updateInspection(inspection);
            inspectionContext.setFindings(inspection.findings);
            inspectionContext.setTemplate(template);
            inspectionContext.setInspectionLoaded(true);

            projectContext.setProject(inspection.project);
            projectContext.setGuestProjectUUID(projectGuestData.link_uuid);
            projectContext.setTemplate(projectGuestData.template);
            appDivisionContext.saveAppDivisionId(projectGuestData.project.divisionProject.divisionId)

            const hasFindingPermissions =
                permCheck("update", "Findings") &&
                permCheck("update", "Close Findings") &&
                user?.roleName === "Guest";

            projectPermissionContext.setReadOnly(!hasFindingPermissions);
        }
    }, [Query.isFetched]);

    useEffect(() => {
        if (questionTypesQuery.isFetched && questionTypesQuery.data) {
            questionTypeContext.setTypes(questionTypesQuery.data);
        }
    }, [questionTypesQuery.isFetched]);

    useEffect(() => {
        const inspection = inspectionContext.inspection;
        const project = projectContext.project;

        const isDataLoaded =
            project &&
            questionTypesQuery.isFetched;

        setLoading(!isDataLoaded);
    }, [inspectionContext.inspection, projectContext.project, questionTypesQuery.isFetched]);

    useEffect(() => {
        return () => {
            projectContext.clear();
            inspectionContext.resetInspection();
            inspectionContext.setTemplate(null);
            Query.remove();
        };
    }, []);

    return (!isGuest || loading) ? (
            <LoadingMessage what="Findings For Guest User" showLoadingText />
        ) : (
            <>
                <PublicURLNavbar />
                <div className="PublicURl__wrapper">
                    <div className="PublicURl">{children}</div>
                </div>
            </>
        );
};

export default NoLayoutWrapper;
